import { TaskStatus, Priority } from '../../types';
import type { Task, TaskListItem, RelatedTaskSummary } from '../../types';
import type {
  TaskListItemResponse,
  TaskResponse,
  RelatedTaskSummaryResponse,
} from '@/types/api/responses';
import type {
  GeometryResponseDto,
  GeometryMapResponseDto,
  RequirementResponseDto,
} from '@/types/api/responses';
import { mapGeometryFromApi } from './geometryMapper';

/**
 * Maps a task list item API response to a frontend TaskListItem.
 * Used for paginated list views where full task detail is not needed.
 */
export function mapTaskListItemFromResponse(
  apiTask: TaskListItemResponse
): TaskListItem {
  return {
    id: apiTask.id,
    shortId: apiTask.shortId,
    title: apiTask.title,
    status: apiTask.status as TaskStatus,
    priority: apiTask.priority as Priority,
    assignedToId: apiTask.assignedToId,
    assignedToName: apiTask.assignedToName,
    assignedToAvatarUrl: apiTask.assignedToAvatarUrl ?? null,
    assignedToIsFormerMember: apiTask.assignedToIsFormerMember,
    startDate: apiTask.startDate ? new Date(apiTask.startDate) : null,
    dueDate: apiTask.dueDate ? new Date(apiTask.dueDate) : null,
    completedAt: apiTask.completedAt ? new Date(apiTask.completedAt) : null,
    location: apiTask.location ?? undefined,
    anchorPoint: {
      lat: apiTask.anchorPoint.lat,
      lng: apiTask.anchorPoint.lng,
    },
    requirementCount: apiTask.requirementCount,
    completedRequirementCount: apiTask.completedRequirementCount,
    attachmentCount: apiTask.attachmentCount,
    commentCount: apiTask.commentCount,
    updatedAt: apiTask.updatedAt ? new Date(apiTask.updatedAt) : null,
  };
}

/**
 * Maps a full TaskResponse (from getById) to a frontend TaskListItem.
 * Computes requirement/attachment/comment counts from nested arrays.
 * Used when we need a TaskListItem but only have the full response.
 */
export function mapTaskResponseToListItem(apiTask: TaskResponse): TaskListItem {
  return {
    id: apiTask.id,
    shortId: apiTask.shortId,
    title: apiTask.title,
    status: apiTask.status as TaskStatus,
    priority: apiTask.priority as Priority,
    assignedToId: apiTask.assignedToId,
    assignedToName: apiTask.assignedToName,
    assignedToAvatarUrl: apiTask.assignedToAvatarUrl ?? null,
    assignedToIsFormerMember: apiTask.assignedToIsFormerMember,
    startDate: apiTask.startDate ? new Date(apiTask.startDate) : null,
    dueDate: apiTask.dueDate ? new Date(apiTask.dueDate) : null,
    completedAt: apiTask.completedAt ? new Date(apiTask.completedAt) : null,
    location: apiTask.location ?? undefined,
    anchorPoint: {
      lat: apiTask.anchorPoint.lat,
      lng: apiTask.anchorPoint.lng,
    },
    requirementCount: apiTask.requirements.length,
    completedRequirementCount: apiTask.requirements.filter(
      (requirement) => requirement.isCompleted
    ).length,
    attachmentCount: apiTask.attachments.length,
    commentCount: apiTask.comments.length,
    updatedAt: apiTask.updatedAt ? new Date(apiTask.updatedAt) : null,
  };
}

/**
 * Minimal API shape shared by TaskResponse and TaskMapResponse. The two sources
 * expose requirement progress differently — TaskResponse carries full
 * `requirements[]`, TaskMapResponse carries pre-computed counts — so the
 * mapper accepts either shape and derives the counts at call time.
 */
interface MapTaskInput {
  id: string;
  shortId: string;
  title: string;
  status: string;
  priority: string;
  assignedToId?: string | null;
  assignedToName: string | null;
  assignedToAvatarUrl: string | null;
  assignedToIsFormerMember: boolean;
  dueDate: string | null;
  anchorPoint: { lat: number; lng: number };
  geometries: (GeometryResponseDto | GeometryMapResponseDto)[];
  requirements?: RequirementResponseDto[];
  requirementCount?: number;
  completedRequirementCount?: number;
}

/**
 * Maps an API task response to the lightweight Task stub stored in mapTasks.
 * Used by both fetchTasksForMap (bulk) and createTaskApi (single push).
 */
export function mapTaskForMap(apiTask: MapTaskInput): Task {
  const requirementCount =
    apiTask.requirementCount ?? apiTask.requirements?.length ?? 0;
  const completedRequirementCount =
    apiTask.completedRequirementCount ??
    apiTask.requirements?.filter((requirement) => requirement.isCompleted)
      .length ??
    0;

  return {
    id: apiTask.id,
    shortId: apiTask.shortId,
    title: apiTask.title,
    description: '',
    status: apiTask.status as TaskStatus,
    priority: apiTask.priority as Priority,
    assignedToId: apiTask.assignedToId ?? null,
    assignedToName: apiTask.assignedToName,
    assignedToAvatarUrl: apiTask.assignedToAvatarUrl,
    assignedToIsFormerMember: apiTask.assignedToIsFormerMember,
    startDate: null,
    dueDate: apiTask.dueDate ? new Date(apiTask.dueDate) : null,
    completedAt: null,
    anchorPoint: {
      lat: apiTask.anchorPoint.lat,
      lng: apiTask.anchorPoint.lng,
    },
    geometries: apiTask.geometries.map(mapGeometryFromApi),
    requirements: [],
    attachments: [],
    comments: [],
    history: [],
    relatedTasks: [],
    requireRelatedTasksCompletion: false,
    requirementCount,
    completedRequirementCount,
    createdAt: new Date(),
    updatedAt: null,
  };
}

/**
 * Maps a related task summary API response to the frontend RelatedTaskSummary.
 * Used when hydrating related tasks from the task detail endpoint.
 */
export function mapRelatedTaskSummaryFromResponse(
  response: RelatedTaskSummaryResponse
): RelatedTaskSummary {
  return {
    id: response.id,
    shortId: response.shortId,
    title: response.title,
    status: response.status as TaskStatus,
    location: response.location,
    dueDate: response.dueDate ? new Date(response.dueDate) : null,
  };
}
